'use client';

// TopBar — editorial command bar: breadcrumbs · quick-jump search · theme toggle.
// Pure chrome, no feature logic.

import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { isHiddenHref } from '@/lib/hidden_features';

const ALL_ROUTES = [
    { href: '/dashboard', label: 'Dashboard', icon: '📊' },
    { href: '/get-started', label: 'Get Started', icon: '🚀' },
    { href: '/alerts', label: 'Alerts', icon: '🔔' },
    { href: '/lead-finder', label: 'Lead Finder', icon: '🧲' },
    { href: '/leads', label: 'Smart Lead CRM', icon: '🎯' },
    { href: '/reactivation', label: 'Lead Reactivation', icon: '💸' },
    { href: '/competitor-xray', label: 'Competitor X-Ray', icon: '🛰️' },
    { href: '/agency', label: 'Prospecting Hub', icon: '🎯' },
    { href: '/pipeline', label: 'Pipeline & ROI', icon: '📈' },
    { href: '/workflows', label: 'Automation Workflows', icon: '⚡' },
    { href: '/campaigns', label: 'Outreach Campaigns', icon: '✉️' },
    { href: '/broadcasts', label: 'Email Marketing', icon: '📣' },
    { href: '/scheduled', label: 'Scheduled Outreach', icon: '🗓' },
    { href: '/campaigns/analytics', label: 'Campaign Analytics', icon: '📈' },
    { href: '/unibox', label: 'Unified Inbox', icon: '📥' },
    { href: '/approvals', label: 'Approvals', icon: '⚖️' },
    { href: '/bookings', label: 'Booking & Calendar', icon: '📅' },
    { href: '/youtube-studio', label: 'YouTube Creator Kit', icon: '🎬' },
    { href: '/youtube', label: 'YouTube Optimizer', icon: '▶️' },
    { href: '/video-lab', label: 'Viral Video Lab', icon: '🔥' },
    { href: '/script-studio', label: 'Script Studio', icon: '🎬' },
    { href: '/thumbnails', label: 'Thumbnail Studio', icon: '🖼️' },
    { href: '/image-studio', label: 'Image Generator', icon: '🎨' },
    { href: '/voice-studio', label: 'Voice Studio (clone your voice)', icon: '🎙' },
    { href: '/library', label: 'Media Library', icon: '🎞️' },
    { href: '/link-building', label: 'AI Auto Linkbuilding', icon: '🔗' },
    { href: '/agency-hq', label: 'Agency HQ', icon: '🏢' },
    { href: '/proposals', label: 'Proposals', icon: '📝' },
    { href: '/invoices', label: 'Invoices', icon: '🧾' },
    { href: '/clients', label: 'Clients', icon: '🏢' },
    { href: '/portal', label: 'Client Portal', icon: '🪟' },
    { href: '/reports', label: 'Monthly Reports', icon: '📄' },
    { href: '/review-responder', label: 'Reviews', icon: '⭐' },
    { href: '/accounts', label: 'Sender Accounts', icon: '🔑' },
    { href: '/deliverability', label: 'Deliverability', icon: '🛡️' },
    { href: '/system-health', label: 'System Health', icon: '💚' },
    { href: '/models', label: 'AI Models & Connectors', icon: '🧠' },
    { href: '/settings', label: 'Settings', icon: '⚙️' },
    { href: '/upgrade', label: 'License & Plan', icon: '👑' },
];
// Hidden product areas (lib/hidden_features.js) never show up in quick-jump.
const ROUTES = ALL_ROUTES.filter((r) => !isHiddenHref(r.href));


const pretty = (seg) => seg
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function TopBar() {
    const pathname = usePathname() || '/';
    const router = useRouter();
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [dark, setDark] = useState(false);
    const [hi, setHi] = useState(0);
    const wrapRef = useRef(null);

    useEffect(() => {
        try { setDark(document.documentElement.classList.contains('dark')); } catch {}
    }, []);

    useEffect(() => {
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const toggleTheme = () => {
        const next = !dark;
        setDark(next);
        try {
            document.documentElement.classList.toggle('dark', next);
            localStorage.setItem('ol-theme', next ? 'dark' : 'light');
        } catch {}
    };

    const matches = q.trim()
        ? ROUTES.filter(r => r.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
        : [];

    const go = (href) => { setQ(''); setOpen(false); router.push(href); };

    const onKey = (e) => {
        if (!matches.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, matches.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); go(matches[Math.min(hi, matches.length - 1)].href); }
        else if (e.key === 'Escape') setOpen(false);
    };

    // Breadcrumbs from the path: Home / Segment / Sub
    const segs = pathname.split('/').filter(Boolean).slice(0, 3);
    const known = ROUTES.find(r => r.href === pathname);

    return (
        <div className="app-topbar" data-no-i18n>
            {/* Breadcrumbs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
                <Link href="/dashboard" title="Dashboard" style={{ textDecoration: 'none', fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 15, lineHeight: 1, color: 'var(--ink-3)' }}>Home</Link>
                {segs.length === 0 && (
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Dashboard</span>
                )}
                {segs.map((s, i) => {
                    const href = '/' + segs.slice(0, i + 1).join('/');
                    const last = i === segs.length - 1;
                    const label = last && known ? known.label : pretty(decodeURIComponent(s));
                    return (
                        <span key={href} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                            <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>/</span>
                            {last ? (
                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                            ) : (
                                <Link href={href} style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', textDecoration: 'none', whiteSpace: 'nowrap' }}>{label}</Link>
                            )}
                        </span>
                    );
                })}
            </div>

            {/* Quick-jump search */}
            <div ref={wrapRef} style={{ position: 'relative', width: 'min(300px, 34vw)' }}>
                <input
                    suppressHydrationWarning
                    type="text"
                    placeholder="Jump to…"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKey}
                    style={{
                        width: '100%', padding: '6px 14px', borderRadius: 999, minHeight: 34,
                        fontSize: 13, fontWeight: 500,
                    }}
                />
                {open && matches.length > 0 && (
                    <div style={{
                        position: 'absolute', top: 'calc(100% + 6px)', right: 0, left: 0, zIndex: 200,
                        background: 'var(--bg-card)', border: '1px solid var(--hairline-2)', borderRadius: 10,
                        boxShadow: 'var(--shadow-lg)', padding: 4, overflow: 'hidden',
                    }}>
                        {matches.map((m, i) => (
                            <button
                                key={m.href}
                                onMouseEnter={() => setHi(i)}
                                onClick={() => go(m.href)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                                    padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    background: i === hi ? 'var(--bg-subtle)' : 'transparent',
                                    color: 'var(--text-primary)',
                                }}
                            >
                                {m.label}
                                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{m.href}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Theme toggle */}
            <button
                onClick={toggleTheme}
                title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                    height: 34, padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: '1px solid var(--hairline-2)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)',
                }}
            >
                {dark ? 'Light' : 'Ink'}
            </button>
        </div>
    );
}
